// Users Management Page

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { trpc } from '../../lib/trpc';

interface InviteFormData {
  email: string;
  displayName: string;
  role: 'tenant_admin' | 'budget_admin' | 'manager' | 'contributor' | 'user';
}

interface DomainApprovalData {
  domain: string;
  justification: string;
  confirmed: boolean;
}

type InviteStep = 'form' | 'domain-warning' | 'justification' | 'confirm';

const roleLabels: Record<string, string> = {
  tenant_admin: 'Admin',
  budget_admin: 'Budget Admin',
  manager: 'Manager',
  contributor: 'Contributor',
  user: 'User',
};

const statusLabels: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  invited: { label: 'Invited', className: 'bg-yellow-100 text-yellow-800' },
  suspended: { label: 'Suspended', className: 'bg-red-100 text-red-800' },
};

export function UsersPage() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteStep, setInviteStep] = useState<InviteStep>('form');
  const [activeTab, setActiveTab] = useState<'users' | 'requests'>('users');
  const [pendingInviteData, setPendingInviteData] = useState<InviteFormData | null>(null);
  const [domainApproval, setDomainApproval] = useState<DomainApprovalData>({
    domain: '',
    justification: '',
    confirmed: false,
  });
  const [domainInfo, setDomainInfo] = useState<{
    allowedDomains: string[];
    existingDomains: string[];
  } | null>(null);

  const utils = trpc.useUtils();

  // Fetch users
  const usersQuery = trpc.user.list.useQuery({ limit: 50 });

  // Fetch pending access requests
  const accessRequestsQuery = trpc.auth.getPendingAccessRequests.useQuery();

  // Invite user mutation
  const inviteMutation = trpc.user.invite.useMutation({
    onSuccess: (data) => {
      utils.user.list.invalidate();
      closeInviteModal();
      if (data.domainAdded) {
        // Could show a toast notification here
      }
    },
    onError: (error) => {
      // Check if this is a domain approval required error
      if (error.message.startsWith('DOMAIN_APPROVAL_REQUIRED:')) {
        const domain = error.message.split(':')[1];
        // Note: cause data (allowedDomains, existingDomains) is not serialized by tRPC
        // The domain is extracted from the message; other info would need a separate API call
        setDomainApproval((prev) => ({ ...prev, domain: domain ?? '' }));
        setDomainInfo(null);
        setInviteStep('domain-warning');
      }
    },
  });

  // Handle access request mutation
  const handleAccessRequestMutation = trpc.auth.handleAccessRequest.useMutation({
    onSuccess: () => {
      utils.auth.getPendingAccessRequests.invalidate();
      utils.user.list.invalidate();
    },
  });

  // Remove user mutation
  const removeMutation = trpc.user.remove.useMutation({
    onSuccess: () => {
      utils.user.list.invalidate();
    },
  });

  // Resend invitation mutation
  const resendMutation = trpc.user.resendInvitation.useMutation();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<InviteFormData>({
    defaultValues: {
      role: 'user',
    },
  });

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteStep('form');
    setPendingInviteData(null);
    setDomainApproval({ domain: '', justification: '', confirmed: false });
    setDomainInfo(null);
    reset();
    inviteMutation.reset();
  };

  const onInviteSubmit = (data: InviteFormData) => {
    setPendingInviteData(data);
    inviteMutation.mutate(data);
  };

  const onProceedWithDomainApproval = () => {
    setInviteStep('justification');
  };

  const onSubmitJustification = () => {
    if (domainApproval.justification.length < 10) {
      return;
    }
    setInviteStep('confirm');
  };

  const onFinalConfirm = () => {
    if (!pendingInviteData || !domainApproval.confirmed) return;

    inviteMutation.mutate({
      ...pendingInviteData,
      approveDomain: true,
      domainJustification: domainApproval.justification,
    });
  };

  const handleApprove = (invitationId: string) => {
    handleAccessRequestMutation.mutate({
      invitationId,
      action: 'approve',
      role: 'user',
    });
  };

  const handleDecline = (invitationId: string) => {
    if (confirm('Are you sure you want to decline this access request?')) {
      handleAccessRequestMutation.mutate({
        invitationId,
        action: 'decline',
      });
    }
  };

  const handleRemoveUser = (userId: string, email: string) => {
    if (confirm(`Are you sure you want to remove ${email} from the organization?`)) {
      removeMutation.mutate({ userId });
    }
  };

  const handleResendInvite = (userId: string) => {
    resendMutation.mutate({ userId });
  };

  const pendingRequests = accessRequestsQuery.data ?? [];

  // Get error message, filtering out domain approval errors
  const getDisplayError = () => {
    if (!inviteMutation.error) return null;
    if (inviteMutation.error.message.startsWith('DOMAIN_APPROVAL_REQUIRED:')) return null;
    return inviteMutation.error.message;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <p className="text-sm text-gray-500">Manage users and access requests</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary"
        >
          Invite User
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Users
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'requests'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Access Requests
            {pendingRequests.length > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {pendingRequests.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            {/* Step 1: Basic Form */}
            {inviteStep === 'form' && (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite User</h3>
                <form onSubmit={handleSubmit(onInviteSubmit)} className="space-y-4">
                  <div>
                    <label className="label">Email</label>
                    <input
                      type="email"
                      className="input"
                      placeholder="colleague@company.com"
                      {...register('email', { required: 'Email is required' })}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Display Name</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="John Smith"
                      {...register('displayName', { required: 'Name is required' })}
                    />
                    {errors.displayName && (
                      <p className="mt-1 text-sm text-red-600">{errors.displayName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Role</label>
                    <select className="input" {...register('role')}>
                      <option value="user">User</option>
                      <option value="contributor">Contributor</option>
                      <option value="manager">Manager</option>
                      <option value="budget_admin">Budget Admin</option>
                      <option value="tenant_admin">Admin</option>
                    </select>
                  </div>

                  {getDisplayError() && (
                    <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                      {getDisplayError()}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={closeInviteModal}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={inviteMutation.isPending}
                      className="btn-primary"
                    >
                      {inviteMutation.isPending ? 'Checking...' : 'Send Invite'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Step 2: Domain Warning */}
            {inviteStep === 'domain-warning' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">New Email Domain Detected</h3>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-600">
                    The email domain <span className="font-semibold text-gray-900">@{domainApproval.domain}</span> is not
                    currently in your organization's allowed domains list.
                  </p>

                  {domainInfo && (domainInfo.allowedDomains.length > 0 || domainInfo.existingDomains.length > 0) && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Current allowed domains:</p>
                      <div className="flex flex-wrap gap-2">
                        {[...new Set([...domainInfo.allowedDomains, ...domainInfo.existingDomains])].map((d) => (
                          <span key={d} className="inline-flex items-center px-2 py-1 rounded bg-gray-200 text-sm text-gray-700">
                            @{d}
                          </span>
                        ))}
                        {domainInfo.allowedDomains.length === 0 && domainInfo.existingDomains.length === 0 && (
                          <span className="text-sm text-gray-500">No domains configured yet</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Security Notice:</strong> Adding a new domain allows anyone with that email domain
                      to request access to your organization. Only proceed if you trust this domain.
                    </p>
                  </div>

                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={closeInviteModal}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onProceedWithDomainApproval}
                      className="btn-primary"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Justification */}
            {inviteStep === 'justification' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Provide Business Justification</h3>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-600">
                    Please explain why users from <span className="font-semibold">@{domainApproval.domain}</span> should
                    have access to your organization. This will be logged for audit purposes.
                  </p>

                  <div>
                    <label className="label">Justification</label>
                    <textarea
                      className="input min-h-[100px]"
                      placeholder="e.g., External accounting firm providing audit services, Consulting partner for Q4 project..."
                      value={domainApproval.justification}
                      onChange={(e) => setDomainApproval((prev) => ({ ...prev, justification: e.target.value }))}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      {domainApproval.justification.length}/500 characters (minimum 10)
                    </p>
                  </div>

                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => setInviteStep('domain-warning')}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={onSubmitJustification}
                      disabled={domainApproval.justification.length < 10}
                      className="btn-primary"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 4: Final Confirmation */}
            {inviteStep === 'confirm' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Domain Addition</h3>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Inviting</p>
                      <p className="font-medium text-gray-900">{pendingInviteData?.displayName} ({pendingInviteData?.email})</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">New domain to add</p>
                      <p className="font-medium text-gray-900">@{domainApproval.domain}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Justification</p>
                      <p className="text-gray-700">{domainApproval.justification}</p>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={domainApproval.confirmed}
                      onChange={(e) => setDomainApproval((prev) => ({ ...prev, confirmed: e.target.checked }))}
                    />
                    <span className="text-sm text-gray-700">
                      I confirm that I want to add <strong>@{domainApproval.domain}</strong> to my organization's
                      allowed domains and invite this user. I understand this action will be logged.
                    </span>
                  </label>

                  {inviteMutation.error && !inviteMutation.error.message.startsWith('DOMAIN_APPROVAL_REQUIRED:') && (
                    <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                      {inviteMutation.error.message}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => setInviteStep('justification')}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={onFinalConfirm}
                      disabled={!domainApproval.confirmed || inviteMutation.isPending}
                      className="btn-primary"
                    >
                      {inviteMutation.isPending ? 'Adding...' : 'Add Domain & Send Invite'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          {usersQuery.isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : usersQuery.error ? (
            <div className="text-center py-8 text-red-500">{usersQuery.error.message}</div>
          ) : usersQuery.data?.items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">User</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Last Login</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {usersQuery.data?.items.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900">{user.displayName}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {roleLabels[user.role] ?? user.role}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${statusLabels[user.status]?.className ?? 'bg-gray-100 text-gray-800'}`}>
                          {statusLabels[user.status]?.label ?? user.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {user.status === 'invited' ? (
                          <button
                            onClick={() => handleResendInvite(user.globalUserId)}
                            disabled={resendMutation.isPending}
                            className="text-sm text-primary-600 hover:text-primary-700"
                          >
                            Resend Invite
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRemoveUser(user.globalUserId, user.email)}
                            disabled={removeMutation.isPending}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Access Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          {accessRequestsQuery.isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading requests...</div>
          ) : pendingRequests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No pending access requests</div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{request.email}</div>
                      <div className="text-sm text-gray-500">
                        Requested {new Date(request.createdAt).toLocaleDateString()}
                      </div>
                      {request.message && (
                        <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">
                          "{request.message}"
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDecline(request.id)}
                        disabled={handleAccessRequestMutation.isPending}
                        className="btn-secondary text-sm"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => handleApprove(request.id)}
                        disabled={handleAccessRequestMutation.isPending}
                        className="btn-primary text-sm"
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
